import React, { useState } from 'react';
import { X, Upload, FileText, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

const INTERACTION_TYPES = [
  { value: 'call', label: 'Phone Call' },
  { value: 'video_call', label: 'Video Call' },
  { value: 'meeting', label: 'In-Person Meeting' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text/Message' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'coffee', label: 'Coffee/Meal' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' }
];

const SENTIMENTS = [
  { value: 'positive', label: 'Positive', color: 'var(--success)' },
  { value: 'neutral', label: 'Neutral', color: 'var(--gray-400)' },
  { value: 'negative', label: 'Negative', color: 'var(--danger)' }
];

function InteractionForm({ contactId, onClose, onSave }) {
  const { uploadDocument } = useApp();
  const [formData, setFormData] = useState({
    contact_id: contactId,
    type: 'call',
    date: new Date().toISOString().split('T')[0],
    duration_minutes: '',
    subject: '',
    notes: '',
    sentiment: 'neutral',
    location: ''
  });
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      newAttachments.push({
        file,
        name: file.name,
        size: file.size,
        pending: true
      });
    }

    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const interactionData = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null
      };

      await onSave(interactionData);

      // Upload attachments if any (they'll be linked via interaction_id after creation)
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.pending && attachment.file) {
            await uploadDocument(attachment.file, contactId, null, attachment.name);
          }
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving interaction:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Log Interaction</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="form-input form-select"
                  required
                >
                  {INTERACTION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Duration (minutes)</label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="30"
                  min="1"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Office, Zoom, Coffee shop..."
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="form-input"
                placeholder="What did you discuss?"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Sentiment</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {SENTIMENTS.map(sentiment => (
                  <button
                    key={sentiment.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, sentiment: sentiment.value }))}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      border: '2px solid',
                      borderColor: formData.sentiment === sentiment.value ? sentiment.color : 'var(--gray-200)',
                      background: formData.sentiment === sentiment.value ? `${sentiment.color}15` : 'transparent',
                      color: formData.sentiment === sentiment.value ? sentiment.color : 'var(--gray-500)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                  >
                    {sentiment.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-input form-textarea"
                placeholder="Key takeaways, follow-up items, things to remember..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Attachments</label>
                <div style={{
                  border: '2px dashed var(--gray-200)',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <label style={{ cursor: 'pointer', display: 'block' }}>
                    <Upload size={24} style={{ color: 'var(--gray-400)', marginBottom: '0.5rem' }} />
                    <div className="text-sm text-muted">
                      {uploading ? 'Processing...' : 'Click to attach documents'}
                    </div>
                    <div className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>
                      Meeting notes, agendas, presentations, etc.
                    </div>
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.pptx,.ppt"
                      multiple
                      disabled={uploading}
                    />
                  </label>
                </div>

                {attachments.length > 0 && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {attachments.map((attachment, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem 0.75rem',
                          background: 'var(--gray-50)',
                          borderRadius: '0.375rem'
                        }}
                      >
                        <FileText size={16} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="text-sm font-medium" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {attachment.name}
                          </div>
                          <div className="text-xs text-muted">
                            {(attachment.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--danger)', flexShrink: 0 }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
              {saving ? 'Saving...' : 'Log Interaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InteractionForm;
